import React, { useCallback, useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Typography,
    alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
    ArrowBack as ArrowBackIcon,
    EditOutlined as EditIcon,
    PeopleOutline as PeopleIcon,
    FolderOutlined as ProjectIcon,
    PlaceOutlined as SiteIcon,
    BoltOutlined as DeploymentIcon,
    InsertDriveFileOutlined as FileIcon,
    KeyOutlined as KeyIcon,
    BusinessOutlined as BusinessIcon,
    LanguageOutlined as DomainIcon,
    PersonOutline as ContactIcon,
    CalendarTodayOutlined as CalendarIcon,
    CheckCircleOutline as StatusIcon,
    LinkOutlined as LinkIcon,
    OpenInNew as OpenIcon,
    AnalyticsOutlined as AnalyticsIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import clientService from 'src/clientService';
import { OrgMemberResponse, OrganisationResponse } from 'src/api/Api';
import { useSnackbar } from 'src/view/components/SnackbarService';
import {
    ORGANISATION_AVATAR_COLOR,
    formatDate,
    formatStatusLabel,
    getOrganisationInitials,
    getStatusColor,
} from './organisationUtils';

interface ApiErrorShape {
    detail?: { msg?: string }[];
    error?: { message?: string };
    message?: string;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
    const apiError = error as ApiErrorShape;
    return apiError?.detail?.[0]?.msg || apiError?.error?.message || apiError?.message || fallback;
};

const StatCard: React.FC<{
    icon: React.ElementType;
    label: string;
    value: string | number;
    helper: string;
    color: string;
}> = ({ icon: Icon, label, value, helper, color }) => (
    <Box
        sx={{
            p: 2.5,
            bgcolor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '12px',
            minHeight: 118,
        }}
    >
        <Box
            sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(color, 0.12),
                color,
                mb: 2,
            }}
        >
            <Icon sx={{ fontSize: 17 }} />
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', mb: 0.5 }}>
            {label}
        </Typography>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.45rem', lineHeight: 1 }}>
            {value}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: '0.62rem', mt: 0.8 }}>
            {helper}
        </Typography>
    </Box>
);

const InfoItem: React.FC<{
    icon: React.ElementType;
    label: string;
    value: string;
    color?: string;
}> = ({ icon: Icon, label, value, color = '#fff' }) => (
    <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
            <Icon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 15 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase' }}>
                {label}
            </Typography>
        </Box>
        <Typography noWrap sx={{ color, fontSize: '0.9rem', fontWeight: 800 }}>
            {value}
        </Typography>
    </Box>
);

const modules = [
    // Flora Intelligence is hidden for now.
    // {
    //     title: 'FLORA INTELLIGENCE',
    //     description: 'Identify plant species and vegetation health.',
    //     status: 'Enabled',
    //     usage: '184,231',
    //     color: '#10B981',
    //     enabled: true,
    // },
    {
        title: 'SPECIES VISION',
        description: 'Species recognition and biodiversity monitoring.',
        status: 'Enabled',
        usage: '428,541',
        color: '#F59E0B',
        enabled: true,
    },
    {
        title: 'BIOACOUSTICS',
        description: 'Bird and wildlife sound identification.',
        status: 'Enabled',
        usage: '96,312',
        color: '#06B6D4',
        enabled: true,
    },
    {
        title: 'CLIMATE INTELLIGENCE',
        description: 'Climate and ecosystem intelligence.',
        status: 'Enabled',
        usage: '58,714',
        color: '#3B82F6',
        enabled: true,
    },
    {
        title: 'EDNA',
        description: 'Environmental DNA analysis for aquatic biodiversity.',
        status: 'Not Enabled',
        usage: '--',
        color: '#6B7280',
        enabled: false,
    },
    {
        title: 'TNFD ANALYTICS',
        description: 'Nature disclosure analytics and risk assessment.',
        status: 'Not Enabled',
        usage: '--',
        color: '#6B7280',
        enabled: false,
    },
    {
        title: 'TCFD COMPLIANCE',
        description: 'Climate disclosure and reporting module.',
        status: 'Not Enabled',
        usage: '--',
        color: '#6B7280',
        enabled: false,
    },
];

const OrganisationOverviewPage: React.FC = () => {
    const { orgId } = useParams<{ orgId: string }>();
    const navigate = useNavigate();
    const { showSnackbar, snackbarType } = useSnackbar();
    const [organisation, setOrganisation] = useState<OrganisationResponse | null>(null);
    const [members, setMembers] = useState<OrgMemberResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchOverview = useCallback(async () => {
        if (!orgId) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const [orgResponse, memberResponse] = await Promise.all([
                clientService.api.getOrganisationApiV1OrganisationsOrgIdGet(orgId),
                clientService.api.listOrganisationMembersApiV1OrganisationsOrgIdMembersGet(orgId, {
                    skip: 0,
                    limit: 500,
                }),
            ]);
            setOrganisation(orgResponse.data);
            setMembers(memberResponse.data.data || []);
        } catch (err) {
            const errorMessage = getErrorMessage(err, 'Failed to fetch organisation overview.');
            setError(errorMessage);
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [orgId, showSnackbar, snackbarType.ERROR]);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    if (loading && !organisation) {
        return (
            <Box sx={{ px: 4, py: 10, bgcolor: '#0D0D0D', minHeight: '100%', borderRadius: '16px', display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#10B981' }} />
            </Box>
        );
    }

    if (error || !organisation) {
        return (
            <Box sx={{ px: 4, py: 10, bgcolor: '#0D0D0D', minHeight: '100%', borderRadius: '16px', textAlign: 'center' }}>
                <Typography sx={{ color: '#F87171', mb: 2 }}>{error || 'Organisation not found.'}</Typography>
                <Button variant="outlined" onClick={fetchOverview}>Retry</Button>
            </Box>
        );
    }

    const statusColor = getStatusColor(organisation.status);
    const primaryContact = members.find(member => member.role?.toLowerCase().includes('admin'))?.user?.email
        || members[0]?.user?.email
        || '--';

    return (
        <Box sx={{ px: 4, py: 4, bgcolor: '#05090A', minHeight: '100%', borderRadius: '16px' }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/admin/organisations')}
                sx={{ color: '#10B981', textTransform: 'none', fontWeight: 700, mb: 3, px: 0, '&:hover': { bgcolor: 'transparent', color: '#34D399' } }}
            >
                Back to Organisations
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0 }}>
                    <Avatar
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '16px',
                            bgcolor: alpha(ORGANISATION_AVATAR_COLOR, 0.15),
                            color: ORGANISATION_AVATAR_COLOR,
                            fontWeight: 900,
                            fontSize: '1.4rem',
                            border: `1px solid ${alpha(ORGANISATION_AVATAR_COLOR, 0.35)}`,
                        }}
                    >
                        {getOrganisationInitials(organisation.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: '#fff', fontSize: { xs: '1.7rem', md: '2rem' }, fontWeight: 900, lineHeight: 1.1 }}>
                            {organisation.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mt: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.78rem' }}>
                                {organisation.domain || 'No domain configured'}
                            </Typography>
                            <Chip
                                label={formatStatusLabel(organisation.status)}
                                size="small"
                                sx={{
                                    height: 22,
                                    bgcolor: alpha(statusColor, 0.12),
                                    color: statusColor,
                                    border: `1px solid ${alpha(statusColor, 0.28)}`,
                                    fontSize: '0.66rem',
                                    fontWeight: 800,
                                }}
                            />
                            <Typography sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.75rem' }}>
                                Joined Since {formatDate(organisation.created_at)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        startIcon={<EditIcon />}
                        sx={{
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.12)',
                            bgcolor: 'rgba(255,255,255,0.03)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.2,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                        }}
                    >
                        Edit Organisation
                    </Button>
                    <Button
                        startIcon={<PeopleIcon />}
                        onClick={() => navigate(`/admin/organisation/${organisation.org_id}/members`)}
                        sx={{
                            color: '#001D12',
                            border: `1px solid ${alpha('#10B981', 0.4)}`,
                            bgcolor: '#10B981',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 900,
                            px: 2.2,
                            '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        Manage Members
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <StatCard icon={PeopleIcon} label="Total Members" value={members.length} helper="Across organisation" color="#10B981" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <StatCard icon={ProjectIcon} label="Active Projects" value="--" helper="Across organisation" color="#3B82F6" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <StatCard icon={SiteIcon} label="Total Sites" value="--" helper="Across organisation" color="#10B981" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <StatCard icon={DeploymentIcon} label="Active Deployments" value="--" helper="Currently running" color="#6366F1" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <StatCard icon={FileIcon} label="Files Uploaded" value="--" helper="Across all projects" color="#F97316" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                    <StatCard icon={KeyIcon} label="API Keys" value="--" helper="Active keys" color="#A855F7" />
                </Grid>
            </Grid>

            <Box
                sx={{
                    p: 3,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '12px',
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 3 }}>
                    <BusinessIcon sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 19 }} />
                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.05rem' }}>
                        Organisation Information
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={BusinessIcon} label="Organisation Name" value={organisation.name} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={DomainIcon} label="Organisation Domain" value={organisation.domain || '--'} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={ContactIcon} label="Primary Contact" value={primaryContact} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={CalendarIcon} label="Joined Since" value={formatDate(organisation.created_at)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <InfoItem icon={StatusIcon} label="Status" value={formatStatusLabel(organisation.status)} color={statusColor} />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.25rem' }}>
                        Darukaa Modules
                    </Typography>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', mb: 2.5 }}>
                    Tools and AI systems currently enabled for this organisation.
                </Typography>
                <Grid container spacing={2}>
                    {modules.map(module => (
                        <Grid key={module.title} size={{ xs: 12, sm: 6, lg: module.enabled ? 3 : 4 }}>
                            <Box
                                sx={{
                                    minHeight: module.enabled ? 230 : 150,
                                    p: 2.5,
                                    borderRadius: '12px',
                                    border: `1px solid ${alpha(module.enabled ? '#10B981' : '#fff', module.enabled ? 0.28 : 0.07)}`,
                                    bgcolor: module.enabled ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.02)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    opacity: module.enabled ? 1 : 0.58,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: `radial-gradient(circle at 80% 30%, ${alpha(module.color, 0.28)}, transparent 34%), linear-gradient(135deg, ${alpha(module.color, 0.08)}, transparent 62%)`,
                                        pointerEvents: 'none',
                                    }}
                                />
                                <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Chip
                                        label={module.status}
                                        size="small"
                                        sx={{
                                            alignSelf: 'flex-start',
                                            height: 20,
                                            bgcolor: alpha(module.enabled ? '#10B981' : '#6B7280', 0.12),
                                            color: module.enabled ? '#10B981' : '#9CA3AF',
                                            border: `1px solid ${alpha(module.enabled ? '#10B981' : '#6B7280', 0.3)}`,
                                            fontSize: '0.58rem',
                                            fontWeight: 900,
                                            mb: 2.5,
                                        }}
                                    />
                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: module.enabled ? '1.2rem' : '1.1rem', lineHeight: 1.05, mb: 1 }}>
                                        {module.title}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontSize: '0.72rem', lineHeight: 1.45, maxWidth: 210 }}>
                                        {module.description}
                                    </Typography>
                                    {module.enabled ? (
                                        <Box sx={{ mt: 'auto' }}>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.58rem', textTransform: 'uppercase', fontWeight: 900 }}>
                                                Overall Usage
                                            </Typography>
                                            <Typography sx={{ color: '#10B981', fontSize: '1.35rem', fontWeight: 900, mb: 1.5 }}>
                                                {module.usage}
                                                <Box component="span" sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.62rem', ml: 0.5 }}>
                                                    analyses
                                                </Box>
                                            </Typography>
                                            <Button
                                                fullWidth
                                                endIcon={<OpenIcon sx={{ fontSize: 14 }} />}
                                                sx={{
                                                    bgcolor: '#10F48B',
                                                    color: '#001D12',
                                                    borderRadius: '7px',
                                                    textTransform: 'none',
                                                    fontWeight: 900,
                                                    fontSize: '0.72rem',
                                                    '&:hover': { bgcolor: '#10B981' },
                                                }}
                                            >
                                                Open Analytics
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Button
                                            fullWidth
                                            startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                                            sx={{
                                                mt: 'auto',
                                                color: 'rgba(255,255,255,0.45)',
                                                border: '1px solid rgba(255,255,255,0.08)',
                                                borderRadius: '7px',
                                                textTransform: 'none',
                                                fontWeight: 800,
                                                fontSize: '0.72rem',
                                            }}
                                        >
                                            Request Access
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box
                sx={{
                    p: 3,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '12px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2.5 }}>
                    <AnalyticsIcon sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 19 }} />
                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.05rem' }}>
                        API Access
                    </Typography>
                </Box>
                {[
                    ['API Keys Created', '--'],
                    ['Active Endpoints', '--'],
                    ['Last API Activity', '--'],
                ].map(([label, value]) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.8, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.8rem' }}>{label}</Typography>
                        <Typography sx={{ color: '#10B981', fontSize: '0.8rem', fontWeight: 900 }}>{value}</Typography>
                    </Box>
                ))}
                <Button
                    fullWidth
                    endIcon={<OpenIcon sx={{ fontSize: 14 }} />}
                    onClick={() => navigate('/admin/api-management')}
                    sx={{
                        mt: 2.5,
                        color: '#10B981',
                        border: `1px solid ${alpha('#10B981', 0.35)}`,
                        borderRadius: '7px',
                        textTransform: 'none',
                        fontWeight: 900,
                        '&:hover': { bgcolor: alpha('#10B981', 0.08) },
                    }}
                >
                    Open API Management
                </Button>
            </Box>
        </Box>
    );
};

export default OrganisationOverviewPage;
